'use client'
// =============================================================================
// LastCityCard — fiche de la dernière ville analysée, affichée à droite du
// radar sur l'accueil. Toutes les données proviennent de la BD (Municipality :
// municipalProfile + municipalContacts) si la ville a été scannée.
// Le hook useLastCity est partagé avec HeroBanner (liens fiche/histoire/attraits).
// =============================================================================
import { useState, useEffect } from 'react'
import { useCountUp } from '@/hooks/useCountUp'

const ADM = '/api/legal-admin'

interface CityListItem { slug: string; name?: string; nom?: string; lastIngested?: string }

interface CivicContact { name?: string; phone?: string; address?: string; note?: string }
interface Profile {
  identity?:     { officialName?: string | null; type?: string | null; incorporationDate?: string | null; administrativeRegion?: string | null; mrc?: string | null }
  geography?:    { latitude?: number | null; longitude?: number | null; surfaceKm2?: number | null }
  demographics?: { population?: number | null }
  health?:       { hospital?: CivicContact[] }
  services?:     { police?: CivicContact }
  tourism?:      { attractions?: string[] }
}
interface Contacts {
  hotelDeVille?: { adresse?: string; telephone?: string; courriel?: string; siteWeb?: string }
  police?:       { organisation?: string; telephone?: string; adresse?: string }
  hopital?:      { nom?: string; adresse?: string; telephone?: string }
}

export interface LastCityData {
  city:      CityListItem | null
  profile:   Profile | null
  contacts:  Contacts | null
  website:   string | null
  name:      string
  ficheHref: string
}

// ─── Hook partagé : dernière ville analysée + profil + contacts ───────────────

export function useLastCity(): LastCityData {
  const [city, setCity]         = useState<CityListItem | null>(null)
  const [profile, setProfile]   = useState<Profile | null>(null)
  const [contacts, setContacts] = useState<Contacts | null>(null)
  const [website, setWebsite]   = useState<string | null>(null)

  useEffect(() => {
    fetch(`${ADM}/cities?page=1&limit=100`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        const items: CityListItem[] = Array.isArray(d?.items) ? d.items : []
        if (!items.length) return
        // Dernière ville analysée = lastIngested le plus récent
        const latest = [...items].sort((a, b) =>
          new Date(b.lastIngested ?? 0).getTime() - new Date(a.lastIngested ?? 0).getTime()
        )[0]
        setCity(latest)
        fetch(`${ADM}/cities/${encodeURIComponent(latest.slug)}/profile`)
          .then(r => r.ok ? r.json() : null)
          .then(p => { setProfile(p?.profile ?? null); if (p?.website) setWebsite(p.website) })
          .catch(() => {})
        fetch(`${ADM}/cities/${encodeURIComponent(latest.slug)}/contacts`)
          .then(r => r.ok ? r.json() : null)
          .then(c => { setContacts(c?.contacts ?? null); if (c?.website) setWebsite(c.website) })
          .catch(() => {})
      })
      .catch(() => {})
  }, [])

  const name = city ? (city.name ?? city.nom ?? city.slug) : ''
  return {
    city, profile, contacts, website, name,
    ficheHref: name ? `/technologue/villes?tab=municipalites&city=${encodeURIComponent(name)}` : '',
  }
}

const GOLD = '#e8b64c'

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <span style={{ fontSize: 9, color: '#64748b', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 12, color: '#e2e8f0', fontFamily: 'var(--font-mono)', textAlign: 'right', minWidth: 0 }}>{children}</span>
    </div>
  )
}

function PopulationValue({ value }: { value: number }) {
  const shown = useCountUp(value, 1600)
  return <span style={{ color: '#2dd4bf', fontWeight: 800, fontSize: 14 }}>{shown.toLocaleString('fr-CA')}</span>
}

function AddressLine({ icon, title, address, phone }: { icon: string; title: string; address?: string; phone?: string }) {
  if (!address && !phone && !title) return null
  return (
    <div style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <span style={{ fontSize: 13, flexShrink: 0 }}>{icon}</span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#cbd5e1', fontFamily: 'var(--font-mono)' }}>{title}</div>
        {address && <div style={{ fontSize: 10, color: '#64748b', fontFamily: 'var(--font-mono)', lineHeight: 1.5 }}>{address}</div>}
        {phone && <div style={{ fontSize: 10, color: '#4f7cff', fontFamily: 'var(--font-mono)' }}>{phone}</div>}
      </div>
    </div>
  )
}

function extractDomain(url?: string | null): string | null {
  if (!url) return null
  try { return new URL(url.startsWith('http') ? url : `https://${url}`).hostname.replace(/^www\./, '') } catch { return null }
}

export function LastCityCard({ data }: { data: LastCityData }) {
  const { city, profile, contacts, website, name, ficheHref } = data
  if (!city) return null

  const id      = profile?.identity
  const geo     = profile?.geography
  const pop     = profile?.demographics?.population ?? null
  const hopital = contacts?.hopital?.nom || contacts?.hopital?.adresse
    ? { title: contacts?.hopital?.nom ?? 'Hôpital', address: contacts?.hopital?.adresse, phone: contacts?.hopital?.telephone }
    : profile?.health?.hospital?.[0]
      ? { title: profile.health.hospital[0].name ?? 'Hôpital', address: profile.health.hospital[0].address, phone: profile.health.hospital[0].phone }
      : null
  const police  = contacts?.police?.organisation || contacts?.police?.telephone || profile?.services?.police?.name
    ? {
        title:   contacts?.police?.organisation ?? profile?.services?.police?.name ?? 'Sûreté du Québec',
        address: contacts?.police?.adresse ?? profile?.services?.police?.address,
        phone:   contacts?.police?.telephone ?? profile?.services?.police?.phone,
      }
    : null

  // Armoiries / logo de la municipalité — favicon du site officiel (en BD)
  const domain   = extractDomain(contacts?.hotelDeVille?.siteWeb ?? website)
  const crestUrl = domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : null

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      padding: '16px 18px', margin: '20px 0 18px',
      background: 'rgba(3, 9, 22, 0.6)',
      border: `1px solid ${GOLD}30`, borderRadius: 16,
      boxShadow: '0 0 30px rgba(8,20,45,0.6)',
      position: 'relative',
    }}>
      {/* Armoiries de la ville — coin supérieur droit */}
      {crestUrl && (
        <a href={ficheHref} title={`Armoiries — ${name}`} style={{
          position: 'absolute', top: 14, right: 14,
          width: 48, height: 48, borderRadius: 10,
          background: 'rgba(255,255,255,0.04)',
          border: `1px solid ${GOLD}35`,
          display: 'grid', placeItems: 'center',
          boxShadow: `0 0 14px ${GOLD}25`,
        }}>
          <img src={crestUrl} alt={`Armoiries de ${name}`} width={36} height={36}
            style={{ objectFit: 'contain', filter: 'drop-shadow(0 0 6px rgba(79,124,255,0.4))' }}
            onError={e => { (e.currentTarget.parentElement as HTMLElement).style.display = 'none' }} />
        </a>
      )}

      {/* En-tête : dernière ville analysée */}
      <div style={{ fontSize: 8, color: GOLD, fontFamily: 'var(--font-mono)', letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: GOLD, boxShadow: `0 0 5px ${GOLD}`, animation: 'hudPulse 2.5s ease-in-out infinite' }} />
        Dernière ville analysée
      </div>

      {/* 1 — Nom */}
      <a href={ficheHref} style={{ textDecoration: 'none' }}>
        <div className="font-display" style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em', marginBottom: 2, paddingRight: 56 }}>
          {name}
        </div>
      </a>
      {(id?.type || id?.mrc) && (
        <div style={{ fontSize: 9, color: '#475569', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>
          {[id?.type, id?.mrc ? `MRC ${id.mrc}` : null].filter(Boolean).join(' · ')}
        </div>
      )}

      {/* 2-6 — Fiche */}
      <div style={{ marginBottom: 10 }}>
        <Row label="Population est.">
          {pop != null ? <PopulationValue value={pop} /> : <span style={{ color: '#475569' }}>—</span>}
        </Row>
        <Row label="Superficie terre">
          {geo?.surfaceKm2 != null ? `${geo.surfaceKm2.toLocaleString('fr-CA')} km²` : '—'}
        </Row>
        <Row label="Latitude · Longitude">
          {geo?.latitude != null && geo?.longitude != null
            ? `${geo.latitude.toFixed(4)}, ${geo.longitude.toFixed(4)}`
            : '—'}
        </Row>
        <Row label="Incorporation">
          {id?.incorporationDate ?? '—'}
        </Row>
        <Row label="Région admin.">
          {id?.administrativeRegion ?? '—'}
        </Row>
      </div>

      {/* 7 — Adresses clés */}
      <div>
        <AddressLine icon="🏢" title="Hôtel de Ville"
          address={contacts?.hotelDeVille?.adresse}
          phone={contacts?.hotelDeVille?.telephone} />
        {hopital && <AddressLine icon="🏨" title={hopital.title} address={hopital.address} phone={hopital.phone} />}
        {police  && <AddressLine icon="🚔" title={police.title}  address={police.address}  phone={police.phone} />}
      </div>
    </div>
  )
}
